/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { format } from "date-fns";

import {
  status as bidStatus,
  division as userDivision,
  dateFormat
} from "../../../helpers/types";
import SpinnerButton from "./SpinnerButton";
import ErrorMessage from "./ErrorMessage";
import SelfPricedEarlyCloseBidActions from "./SelfPricedEarlyCloseBidActions";
import {
  acceptPendingBid,
  archiveRejectedBid,
  resetBannerActionState
} from "../../../slices/bidSummarySlice";
import messages from "../../../helpers/messages";

function SalesViewRejectedBidState({
  summary,
  division,
  role,
  user,
  showCloseBidInsteadOfArchive = false
}) {
  const dispatch = useDispatch();
  const {
    acceptActionIsLoading,
    acceptActionFailure,
    archiveActionIsLoading,
    archiveActionFailure
  } = useSelector(state => state.bidSummary);
  const route = useLocation().pathname.split("/")[3] || "";

  const { modes, bidId } = summary;
  const {
    lastStatusChangeDateUTC,
    lastStatusChangeByUserName,
    bidModeId
  } = modes.find(x => x.status === bidStatus.rejected);
  const date = format(
    new Date(lastStatusChangeDateUTC + "Z"),
    division === userDivision.coy ? dateFormat.MMddyy : dateFormat.ddMMyy
  );

  const handlePriceMyselfClick = useCallback(
    (bidModeId, bidId) => {
      dispatch(resetBannerActionState());
      dispatch(acceptPendingBid(bidModeId, bidId, role, route));
    },
    [dispatch, role, route]
  );

  const handleArchiveBidClick = useCallback(
    (bidModeId, bidId) => {
      dispatch(resetBannerActionState());
      dispatch(archiveRejectedBid(bidModeId, bidId, role, route));
    },
    [dispatch, role, route]
  );

  return (
    <>
      <p className="inline-block font-size-14 pt-16 mb-0">
        {acceptActionFailure || archiveActionFailure ? (
          <ErrorMessage />
        ) : (
          <FormattedMessage
            {...messages.bidRejectedMessage}
            values={{
              lastStatusChangeByUserName: lastStatusChangeByUserName,
              date: date
            }}
          />
        )}
      </p>
      <div className="fr py-8 my-2">
        {acceptActionIsLoading ? (
          <SpinnerButton minWidth="108px" />
        ) : (
          <button
            className="text-button univers-67-bold-cond font-size-14 uppercase mx-8 h-32 short-button px-12"
            data-testid="price-myself"
            onClick={() => handlePriceMyselfClick(bidModeId, bidId)}
          >
            {messages.priceMyself.defaultMessage}
          </button>
        )}
        {showCloseBidInsteadOfArchive && user ? (
          <SelfPricedEarlyCloseBidActions
            summary={summary}
            user={user}
            forceCrmCloseLostForm={true}
          />
        ) : archiveActionIsLoading ? (
          <SpinnerButton minWidth="108px" />
        ) : (
          <button
            className="text-button univers-67-bold-cond font-size-14 uppercase mx-8 h-32 short-button px-16"
            data-testid="archive-bid"
            onClick={() => handleArchiveBidClick(bidModeId, bidId)}
          >
            {messages.archiveBid.defaultMessage}
          </button>
        )}
      </div>
    </>
  );
}

export default SalesViewRejectedBidState;
